import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './ToastNotification.css';

// Simple confetti particle component for friend-joined celebration
const ConfettiParticle = ({ index, color }) => {
  const randomX = Math.random() * 100;
  const randomDelay = Math.random() * 0.5;
  const randomDuration = 1.5 + Math.random() * 1;
  const randomRotation = Math.random() * 360;

  return (
    <div
      style={{
        position: 'absolute',
        left: `${randomX}%`,
        top: '-10px',
        width: '8px',
        height: '8px',
        backgroundColor: color,
        borderRadius: index % 2 === 0 ? '50%' : '2px',
        animation: `confetti-fall ${randomDuration}s ease-out ${randomDelay}s forwards`,
        transform: `rotate(${randomRotation}deg)`,
        opacity: 0.9,
      }}
    />
  );
};

const ToastNotification = ({
  message,
  type = 'info',
  duration = 4000,
  onClose,
  show = true,
  actionButton = null, // { label: 'Button Text', onClick: () => {} }
}) => {
  const [isVisible, setIsVisible] = useState(true); // Start visible immediately
  const [isExiting, setIsExiting] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  // Confetti colors - fun, celebratory palette
  const confettiColors = ['#ff6b6b', '#4ecdc4', '#ffe66d', '#95e1d3', '#f38181', '#aa96da', '#fcbad3'];

  // Trigger confetti for friend-joined
  useEffect(() => {
    if (show && type === 'friend-joined') {
      setShowConfetti(true);
      // Hide confetti after animation completes
      const timer = setTimeout(() => setShowConfetti(false), 2500);
      return () => clearTimeout(timer);
    }
  }, [show, type]);

  useEffect(() => {
    if (show && message) {
      // Auto-hide after duration
      const hideTimer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(hideTimer);
    }
  }, [show, message, duration]);

  const handleClose = () => {
    setIsExiting(true);
    
    // Wait for exit animation to complete
    setTimeout(() => {
      setIsVisible(false);
      setIsExiting(false);
      if (onClose) onClose();
    }, 300);
  };

  if (!show || !message || !isVisible) return null;

  // Get icon based on toast type
  const getIcon = () => {
    switch (type) {
      case 'friend-joined':
        return '🎉';
      case 'success':
        return '✅';
      case 'warning':
        return '⚠️';
      case 'error':
        return '❌';
      case 'video':
        return '📹';
      default:
        return 'ℹ️';
    }
  };

  // Get background color based on type
  const getTypeClass = () => {
    switch (type) {
      case 'friend-joined':
        return 'toast-friend-joined';
      case 'success':
        return 'toast-success';
      case 'warning':
        return 'toast-warning';
      case 'error':
        return 'toast-error';
      case 'video':
        return 'toast-video';
      default:
        return 'toast-info';
    }
  };

  // Friend-joined specific styling (2025 Discord/FaceTime celebration style)
  const getFriendJoinedStyle = () => {
    if (type === 'friend-joined') {
      return {
        background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.95), rgba(6, 182, 212, 0.95))',
        border: '2px solid rgba(255, 255, 255, 0.3)',
        color: 'white',
      };
    }
    return {};
  };

  const toastContent = (
    <div
      className={`toast-notification ${getTypeClass()} toast-enter`}
      style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        transform: 'translateX(0)',
        opacity: 1,
        zIndex: 10002,
        overflow: 'visible',

        // Learning Aquarium glass morphism
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(245, 249, 250, 0.98))',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '2px solid #0EA5E9',
        borderRadius: '16px',
        boxShadow: '0 16px 64px rgba(14, 165, 233, 0.2), 0 8px 32px rgba(15, 23, 42, 0.1)',

        // Typography
        color: '#0F172A',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        fontSize: '15px',
        fontWeight: '500',

        // Layout
        minWidth: '320px',
        maxWidth: '400px',
        padding: '20px 24px',

        // Smooth animations
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

        // Friend-joined override
        ...getFriendJoinedStyle()
      }}
      onClick={handleClose}
    >
      {/* Confetti celebration for friend-joined - Discord/FaceTime style */}
      {showConfetti && type === 'friend-joined' && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '150px',
          overflow: 'hidden',
          pointerEvents: 'none',
          zIndex: 1
        }}>
          {[...Array(20)].map((_, i) => (
            <ConfettiParticle
              key={i}
              index={i}
              color={confettiColors[i % confettiColors.length]}
            />
          ))}
        </div>
      )}
      {/* Icon - Apple/Google minimal style */}
      <div style={{
        fontSize: '20px',
        marginRight: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '32px',
        height: '32px',
        background: 'rgba(14, 165, 233, 0.1)',
        borderRadius: '8px'
      }}>
        {getIcon()}
      </div>
      
      {/* Content */}
      <div className="toast-content" style={{ flex: 1 }}>
        <div className="toast-message">
          {message}
        </div>

        {/* Action button if provided */}
        {actionButton && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              actionButton.onClick();
            }}
            style={{
              marginTop: '12px',
              padding: '10px 20px',
              background: 'linear-gradient(135deg, #0EA5E9 0%, #0284C7 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(14, 165, 233, 0.3)',
              width: '100%'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 4px 12px rgba(14, 165, 233, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 8px rgba(14, 165, 233, 0.3)';
            }}
          >
            {actionButton.label}
          </button>
        )}

        {/* Progress bar for auto-close timing */}
        {!isExiting && !actionButton && (
          <div
            className="toast-progress-bar"
            style={{
              animationDuration: `${duration}ms`
            }}
          />
        )}
      </div>
      
      {/* Close button - Learning Aquarium style */}
      <div 
        style={{
          position: 'absolute',
          top: '12px',
          right: '12px',
          width: '28px',
          height: '28px',
          borderRadius: '8px',
          background: 'rgba(15, 23, 42, 0.1)',
          border: '1px solid rgba(15, 23, 42, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '14px',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          color: '#64748B',
          fontWeight: '600'
        }}
        onClick={(e) => {
          e.stopPropagation();
          handleClose();
        }}
        onMouseEnter={(e) => {
          e.target.style.background = 'rgba(15, 23, 42, 0.15)';
          e.target.style.transform = 'translateY(-1px)';
          e.target.style.boxShadow = '0 4px 8px rgba(15, 23, 42, 0.1)';
        }}
        onMouseLeave={(e) => {
          e.target.style.background = 'rgba(15, 23, 42, 0.1)';
          e.target.style.transform = 'translateY(0)';
          e.target.style.boxShadow = 'none';
        }}
      >
        ✕
      </div>
    </div>
  );

  // Use portal to render at document root for proper z-index
  return createPortal(toastContent, document.body);
};

export default ToastNotification;